import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';

import PricesContainer from 'modules/Prices/components/container';
import PriceFilters from 'modules/Prices/pages/price/components/Filters';
import PriceMarket from 'modules/Prices/pages/price/components/GraphByMarkets';
import PriceNetwork from 'modules/Prices/pages/price/components/GraphByNetworks';

import { usePrices, usePricesFilters } from 'apiTyped/hooks/prices';
import { components } from 'apiTyped/types';
import { FormFilters } from 'constants/prices';
import { RivalsByVintage } from 'interface/price';

const PriceDiv = styled.div`
  position: relative;
  margin-bottom: 40px;

  > *:not(:last-child) {
    margin-bottom: 20px;
  }

  .chartsContainer {
    position: relative;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
    grid-gap: 20px;
  }

  @media (max-width: 1550px) {
    .chartsContainer {
      grid-template-columns: 1fr;
    }
  }
`;

const PricesPrice = () => {
  const [wineId, setWineId] = useState<number | null>(null);
  const [vintage, setVintage] = useState<string | null>(null);
  const [format, setFormat] = useState<string | null>(null);
  const [competitor, setCompetitor] = useState<string | null>(null);
  const [competitorVintage, setCompetitorVintage] = useState<string | null>(
    null
  );
  const [filtersLoading, setFiltersLoading] = useState<boolean>(false);

  const [formFilters, setFormFilters] = useState<FormFilters | null>(null);
  const [avgPricesByNetworks, setAvgPricesByNetworks] = useState<
    components['schemas']['DataDetail'][]
  >([]);
  const [avgPricesByMarkets, setAvgPricesByMarkets] = useState<
    components['schemas']['DataDetail'][]
  >([]);
  const [rivalsVintages, setRivalsVintages] = useState<RivalsByVintage>({});

  const {
    isLoadingPricesFilters,
    filters,
    isCampaign: showVintages
  } = usePricesFilters();

  const { isLoadingPrices, data } = usePrices(
    !!(formFilters && formFilters.wineId && formFilters.vintage),
    formFilters?.wineId,
    formFilters?.vintage
  );

  const wineName = get(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters.find((item: any) => item.wine_id === wineId),
    'wine_name',
    null
  );

  const exportName = [vintage, wineName, format]
    .filter((item) => item)
    .join(' - ');

  const updateFormFilters = (values: FormFilters) => {
    setFiltersLoading(true);
    const onUpdateFilters = () => setFormFilters(values);
    onUpdateFilters();
  };

  useEffect(() => {
    if (
      isLoadingPrices ||
      data == null ||
      !data?.data ||
      !data?.data_detail ||
      !data?.rivals_vintages ||
      !formFilters
    ) {
      return;
    }

    setFiltersLoading(false);
    setWineId(formFilters.wineId || null);
    setVintage(formFilters.vintage || null);
    setFormat(formFilters.format || null);

    setAvgPricesByNetworks(data.data || null);
    setAvgPricesByMarkets(data.data_detail || null);
    setRivalsVintages(data.rivals_vintages || null);
    setAvgPricesByNetworks(data.data || null);
    setAvgPricesByMarkets(data.data_detail || null);
  }, [data]);

  useEffect(() => {
    if (formFilters === null) return;
    if (formFilters.format === format) return;

    setFormat(formFilters.format || null);
    setFiltersLoading(false);
  }, [formFilters]);

  return (
    <PricesContainer>
      <PriceDiv>
        <PriceFilters
          filters={filters}
          onChange={updateFormFilters}
          disabled={isLoadingPricesFilters || isLoadingPrices}
        />
        <div className="chartsContainer">
          <PriceNetwork
            data={avgPricesByNetworks}
            wine={wineName}
            vintage={vintage}
            format={format}
            competitor={competitor}
            competitorVintage={competitorVintage}
            vintagesByWine={rivalsVintages}
            showVintages={showVintages}
            exportName={exportName}
            isLoading={
              isLoadingPricesFilters || filtersLoading || isLoadingPrices
            }
            setCompetitor={setCompetitor}
            setCompetitorVintage={setCompetitorVintage}
          />
          <PriceMarket
            data={avgPricesByMarkets}
            format={format}
            vintage={vintage}
            wine={wineName}
            competitor={competitor}
            competitorVintage={competitorVintage}
            vintagesByWine={rivalsVintages}
            showVintages={showVintages}
            exportName={exportName}
            isLoading={
              isLoadingPricesFilters || filtersLoading || isLoadingPrices
            }
            setCompetitor={setCompetitor}
            setCompetitorVintage={setCompetitorVintage}
          />
        </div>
      </PriceDiv>
    </PricesContainer>
  );
};

export default PricesPrice;
